import { ReplayViewMode } from '@common/Replay/types';
import { NEW_REPLAY_ID } from '@common/Replay/utils';
import { Entity } from '@common/types/Entity';
import { Public_Routes } from '@common/url';
import { Dialog } from '@prophecy/ui-v3/Dialog';
import { BASE_PATH, navigateAndRefresh } from '@prophecy/utils/history';
import { matchPath } from 'react-router-dom';

import { getReplayDetails } from '../../../../data/apis/api';
import { replayToken } from '../../../../utils/replayToken';

let tokenRefreshTimer: NodeJS.Timeout | null = null;
const REFRESH_TOKEN_INTERVAL = 50 * 60 * 1000;

export async function getReplayDetailsUrl(replayId: string) {
  let response = undefined;
  if (replayId === NEW_REPLAY_ID) return;

  try {
    response = await getReplayDetails(replayId);
  } catch (error) {
    const allowedDialogResult = await Dialog.warning({
      title: 'Unable to Access Replay',
      'data-test-id': 'unable-access-toast',
      subTitle:
        "This replay is either unavailable or you don't have permission to view it. Please check the replay link or contact the replay owner.",
      children: null,
      closeButton: null,
      size: 'l',
      closable: false,
      okButton: { children: 'Continue', block: false }
    });
    if (allowedDialogResult === Dialog.ResponseAction.okButton) {
      navigateAndRefresh(Public_Routes.SignIn.getUrl(undefined));
    }
    return;
  }

  const { jwtToken, hasDirectAccess, replayDetails, loggedInUser } = response;
  const url = buildReplayUrl({
    projectUID: replayDetails.projectUID,
    replayId: replayDetails.replayId,
    pipelineName: replayDetails.pipelineName
  });

  if (jwtToken && !hasDirectAccess) {
    replayToken.set(jwtToken);
    scheduleNextRefresh(replayId);
  }
  replayToken.replayDetails = replayDetails;
  replayToken.hasDirectAccess = Boolean(hasDirectAccess);
  replayToken.loggedInUserID = loggedInUser;

  return { url, loggedInUser };
}

export const getReplayId = (pathname: string, searchParams: URLSearchParams) => {
  if (pathname.includes('/replay/')) {
    const replayId = pathname.split('/').at(-1);
    return replayId ?? null;
  }
  const replayIdFromParams = searchParams.get('replayId');
  return replayIdFromParams ?? null;
};

export const isSharedReplay = (url: string) => {
  const targetUrl = new URL(BASE_PATH + url, window.location.origin);

  const currentUrl = new URL(window.location.href);
  const currentReplayMode = currentUrl.searchParams.get('replayMode');

  const pathMatches = matchPath({ path: targetUrl.pathname, end: true }, window.location.pathname);

  // Check if search params match
  const paramsMatch =
    targetUrl.searchParams.get('entity') === currentUrl.searchParams.get('entity') &&
    targetUrl.searchParams.get('name') === currentUrl.searchParams.get('name');

  if (currentReplayMode === ReplayViewMode.edit) {
    return Boolean(pathMatches && paramsMatch && replayToken.hasDirectAccess);
  }

  return Boolean(pathMatches && paramsMatch);
};

export const buildReplayUrl = ({
  projectUID,
  replayId,
  pipelineName,
  replayMode = ReplayViewMode.view
}: {
  projectUID: string;
  replayId: string;
  pipelineName: string;
  replayMode?: ReplayViewMode;
}) => {
  const url = new URL(window.location.href);
  const isOnboardingTour = url.searchParams.get('onboardingTour') === 'true';

  const entityURI = `entity=${Entity.Pipeline.toLowerCase()}&name=${pipelineName}`;
  const baseUrl = `/sql/${projectUID}`;
  const mode = `&replayMode=${replayMode}`;
  const replayIdParam = replayId ? `&replayId=${replayId}` : '';
  const onboardingTourParam = isOnboardingTour ? `&onboardingTour=true` : '';

  return `${baseUrl}?${entityURI}${mode}${replayIdParam}${onboardingTourParam}`;
};

const refreshReplayToken = async (replayId: string) => {
  if (!replayId) return;

  try {
    const response = await getReplayDetails(replayId);
    if (response?.jwtToken) {
      replayToken.set(response.jwtToken);
      scheduleNextRefresh(replayId);
    }
    replayToken.hasDirectAccess = Boolean(response?.hasDirectAccess);
    replayToken.replayDetails = response?.replayDetails;
    replayToken.loggedInUserID = response?.loggedInUser;
  } catch (error) {
    console.error('Error refreshing replay token:', error);
    throw error;
  }
};

function scheduleNextRefresh(replayId: string) {
  if (!replayId) return;
  if (tokenRefreshTimer) {
    clearTimeout(tokenRefreshTimer);
  }
  // Set timeout to refresh token after 50 minutes (10 minutes before 60-minute expiry)
  tokenRefreshTimer = setTimeout(async () => {
    try {
      await refreshReplayToken(replayId);
    } catch (error) {
      console.error('Failed to refresh replay token:', error);
    }
  }, REFRESH_TOKEN_INTERVAL);
}
